import { useCallback, useEffect, useRef, useState } from 'react'

import { CardHeroVariant } from './cardDetailHeroConstants'

/** Auto-close so revealed credentials never sit exposed indefinitely. */
const AUTO_CLOSE_MS = 15000

type CardRevealDisclosure = {
  /** The variant that currently owns the open reveal, or `null` when closed. */
  anchor: CardHeroVariant | null
  /** True when any variant's reveal is open. */
  open: boolean
  /** True only for the variant that currently owns the reveal. */
  isOpenFor: (variant: CardHeroVariant) => boolean
  /**
   * Toggle the reveal for a variant: open it (moving the anchor to it), or — if
   * it is already the open variant — close it.
   */
  toggle: (variant: CardHeroVariant) => void
  close: () => void
  /**
   * The outside-click / Escape container ref for a variant. Put it on the element
   * wrapping BOTH that variant's Show-details trigger and its reveal panel.
   */
  containerRefFor: (variant: CardHeroVariant) => React.RefObject<HTMLDivElement>
}

/**
 * Non-modal disclosure state for the card-details reveal, owned by the hero
 * shell so the full hero and the pinned compact bar share one source of truth.
 * Both heroes stay mounted, so the reveal is *anchored* to whichever one opened
 * it: only that variant renders the panel and reports itself open. It is a
 * disclosure, not a modal — the page stays interactive and focus is not trapped.
 * Escape and a genuine outside click close it, plus an auto-close timeout so
 * sensitive values are not left on screen.
 */
export const useCardRevealDisclosure = (
  autoCloseMs: number = AUTO_CLOSE_MS,
): CardRevealDisclosure => {
  const [anchor, setAnchor] = useState<CardHeroVariant | null>(null)
  const fullRef = useRef<HTMLDivElement>(null)
  const compactRef = useRef<HTMLDivElement>(null)

  // Updaters stay pure — React double-invokes them in StrictMode dev and may
  // replay them when a concurrent render is discarded, so no timer bookkeeping
  // lives here. The auto-close timeout is armed declaratively by the effect
  // below, keyed on the anchor.
  const close = useCallback((): void => setAnchor(null), [])
  const toggle = useCallback(
    (variant: CardHeroVariant): void =>
      // Toggling the open variant closes; otherwise open it (moving the anchor
      // here from the other variant).
      setAnchor((current) => (current === variant ? null : variant)),
    [],
  )

  const containerRefFor = useCallback(
    (variant: CardHeroVariant): React.RefObject<HTMLDivElement> =>
      variant === 'full' ? fullRef : compactRef,
    [],
  )

  // Auto-close: while a variant owns the reveal, arm a single timeout that closes
  // it so revealed credentials never sit exposed indefinitely. Keyed on `anchor`,
  // so re-opening (or moving the anchor) re-arms a fresh timer, and every close
  // path (toggle, Escape, outside click, unmount) clears it through the cleanup.
  useEffect(() => {
    if (anchor === null) return undefined
    const timer = setTimeout(() => setAnchor(null), autoCloseMs)
    return () => clearTimeout(timer)
  }, [anchor, autoCloseMs])

  // Escape + outside-click dismissal, attached only while open. The listener is
  // added after the opening click has finished propagating, so it never
  // self-closes on the same click that opened it.
  //
  // A click is "outside" only when it lands outside BOTH hero containers: while
  // one hero owns the reveal the other is inert, but interacting with either
  // hero must never dismiss the panel (the prototype special-cases this so the
  // swap partner can't close an open reveal). Clicks on the page beyond both
  // heroes close it.
  useEffect(() => {
    if (anchor === null) return undefined

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') close()
    }
    const onDocumentClick = (event: MouseEvent): void => {
      const target = event.target as Node
      const insideAHero =
        (fullRef.current?.contains(target) ?? false) ||
        (compactRef.current?.contains(target) ?? false)
      if (!insideAHero) close()
    }

    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('click', onDocumentClick)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('click', onDocumentClick)
    }
  }, [anchor, close])

  return {
    anchor,
    open: anchor !== null,
    isOpenFor: (variant) => anchor === variant,
    toggle,
    close,
    containerRefFor,
  }
}
